import type { AppError } from '@/http/errors';
import type { WebinarSessionType } from '@/features/webinar/types';

/**
 * Une session evergreen n'a pas de room LiveKit côté back : lui demander un token modérateur ne
 * renvoie qu'un 403, et la régie evergreen ne monte de toute façon jamais LiveKitRoom. Le type doit
 * être explicitement `LIVE` : au premier rendu la session n'est pas encore dans le store, et un
 * type inconnu assimilé à un live enverrait la requête avant de savoir qu'elle est evergreen.
 */
export function shouldRequestModeratorLiveKitToken(
  sessionType: WebinarSessionType | null | undefined,
  adminChatRole: string | null,
): boolean {
  return adminChatRole === 'moderator' && sessionType === 'LIVE';
}

/**
 * La présence modérateur n'est plus un effet de bord de l'émission du token LiveKit : elle
 * conditionne l'éligibilité au feedback post-live et doit donc être posée en live comme en
 * evergreen. Le back la refuse tant que la session n'est pas démarrée.
 */
export function shouldRecordStaffPresence(
  sessionType: WebinarSessionType | null | undefined,
  adminChatRole: string | null,
  sessionStatus: string | null | undefined,
): boolean {
  if (sessionStatus !== 'IN_PROGRESS') {
    return false;
  }
  return (
    adminChatRole === 'moderator' || (adminChatRole === 'admin' && sessionType === 'EVERGREEN')
  );
}

export const STAFF_PRESENCE_MAX_RETRIES = 2;
export const STAFF_PRESENCE_RETRY_DELAY_MS = 5000;

/**
 * L'éligibilité au feedback post-live dépend de cet enregistrement : une coupure réseau ou un 5xx
 * méritent une relance. Un refus métier (session pas encore démarrée, utilisateur non modérateur)
 * ne changera pas d'avis, on ne martèle pas le back.
 */
export function shouldRetryStaffPresence(error: unknown): boolean {
  const code = (error as AppError | undefined)?.code;
  return code === 'NETWORK' || code === 'SERVER';
}
